/**
 * Shared types and helpers for the P4P tiered (Meets/Exceeds) metric model.
 * Mirrors backend Empower.Business.Models.MetricTargetTier and the
 * metric_attainment_level / target_tier lookup tables (E20-6317).
 *
 * Rendering/status logic is driven off `attainment_rank` (0=Below Meets, 1=Meets, 2=Exceeds),
 * not the raw `metric_attainment_level_id` (1/2/3) - rank is the column the data model
 * explicitly designed as a stable ordering contract, so a future 3rd tier or any lookup-row
 * reshuffle doesn't require touching this file. `metric_attainment_level_id` is still passed
 * through where needed (e.g. as an API filter key), but never used to branch UI behavior.
 */

export const BELOW_MEETS_RANK = 0;
export const MEETS_RANK = 1;
export const EXCEEDS_RANK = 2;

export const MEETS_TIER_LEVEL = 1;
export const EXCEEDS_TIER_LEVEL = 2;

export interface MetricTargetTier {
    target_tier_id: number;
    target_tier_name: string;
    tier_level: number;
    target: number | null;
}

/** Which model a scorecard version uses - never infer this from tier data presence. */
export enum ScorecardCalculationRule {
    CountBased = 1,
    TieredTarget = 2,
}

export const isTieredScorecard = (scorecardCalculationRuleId?: number | null): boolean =>
    scorecardCalculationRuleId === ScorecardCalculationRule.TieredTarget;

// export const getMeetsTarget = (targets?: MetricTargetTier[] | null): number | null =>
//     targets?.find((t) => t.tier_level === MEETS_TIER_LEVEL)?.target ?? null;

// export const getExceedsTarget = (targets?: MetricTargetTier[] | null): number | null =>
//     targets?.find((t) => t.tier_level === EXCEEDS_TIER_LEVEL)?.target ?? null;

/** Normalizes `targets` to an array whether it arrives as a real
 *  MetricTargetTier[] or as a raw JSON string (seen when a caller's API
 *  path hasn't deserialized the SP's json column) — so this util never
 *  crashes regardless of which endpoint is feeding it. */
function normalizeTargets(targets?: MetricTargetTier[] | string | null): MetricTargetTier[] {
    if (Array.isArray(targets)) return targets;
    if (typeof targets === 'string') {
        try {
            const parsed = JSON.parse(targets);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

export const getMeetsTarget = (targets?: MetricTargetTier[] | string | null): number | null =>
    normalizeTargets(targets).find((t) => t.tier_level === MEETS_TIER_LEVEL)?.target ?? null;

export const getExceedsTarget = (targets?: MetricTargetTier[] | string | null): number | null =>
    normalizeTargets(targets).find((t) => t.tier_level === EXCEEDS_TIER_LEVEL)?.target ?? null;

/**
 * Below Meets when rank is missing/null (legacy row, no attainment computed yet) or explicitly 0.
 * Driven by attainment_rank, not metric_attainment_level_id.
 */
export const isBelowMeetsRank = (attainmentRank?: number | null): boolean =>
    attainmentRank == null || attainmentRank <= BELOW_MEETS_RANK;

export const isExceedsRank = (attainmentRank?: number | null): boolean =>
    attainmentRank != null && attainmentRank >= EXCEEDS_RANK;

/**
 * Converts a raw metric value into a 0-100 position on the gauge arc.
 *
 * The backend already orients min/max as "worst -> best" per metric_direction_id (confirmed via
 * API: ascending NPS returns min:0/max:100, descending ACW returns min:600/max:0) - so this
 * single formula is correct for both ascending and descending metrics with no direction branch
 * needed here. Clamped to [0,100] since a value can fall outside min/max (e.g. exceeding Exceeds).
 */
export const getArcPercent = (value: number | null | undefined, min: number, max: number): number => {
    if (value == null || max === min) {
        return 0;
    }
    const raw = ((value - min) / (max - min)) * 100;
    return Math.min(100, Math.max(0, raw));
};

// Exact API label strings for metric_attainment_level_label - the primary key for status
// color/icon per the design spec, not a magic string invented here.
export const ATTAINMENT_LABEL_EXCEEDS = 'Exceeds Target';
export const ATTAINMENT_LABEL_MEETS = 'Meets Target';
export const ATTAINMENT_LABEL_BELOW_MEETS = 'Below Meets';

export type TieredStatusIcon = 'check' | 'cross';

export interface TieredStatusVisual {
    color: string;
    icon: TieredStatusIcon;
}

/**
 * Single source of truth for the tiered status color + icon, shared by the gauge's center-value
 * text and the status line below it so they can never disagree (previously duplicated in both
 * places). Keyed primarily off metric_attainment_level_label per the design spec:
 *   Exceeds Target -> #2AA154, check
 *   Meets Target   -> #916C00, check
 *   Below Meets    -> #E23636, cross
 * Falls back to attainment_rank only if the label is missing/unrecognized (e.g. a legacy row
 * that hasn't been through the tiered engine yet), so rendering never breaks silently.
 */
export const getTieredStatusVisual = (
    label?: string | null,
    attainmentRank?: number | null
): TieredStatusVisual => {
    const normalized = label?.trim();
    if (normalized === ATTAINMENT_LABEL_EXCEEDS) {
        return { color: '#2AA154', icon: 'check' };
    }
    if (normalized === ATTAINMENT_LABEL_MEETS) {
        return { color: '#5279DD', icon: 'check' };
    }
    if (normalized === ATTAINMENT_LABEL_BELOW_MEETS) {
        return { color: '#E23636', icon: 'cross' };
    }
    if (isExceedsRank(attainmentRank)) {
        return { color: '#2AA154', icon: 'check' };
    }
    if (!isBelowMeetsRank(attainmentRank)) {
        return { color: '#5279DD', icon: 'check' };
    }
    return { color: '#E23636', icon: 'cross' };
};
